import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";

import { useAuth } from "@/providers";
import { RobotoLoadingButton } from "@/shared/components/RobotoLoadingButton";
import type { RobotoConfigKey } from "@/shared/domain/config";
import { useOrgConfig, useUpdateOrgConfig } from "@/shared/domain/config/hooks";

import { McpConnections } from "../McpConnections/McpConnections";
import { SettingsPageLayout } from "../SettingsPageLayout";

import { CurrentOrgAiModels } from "./OrgAiModels/CurrentOrgAiModels";

/** The org config keys this page edits; the rest belong to other screens. */
type AISettingsKey = Extract<
  RobotoConfigKey,
  "ai_common_knowledge" | "ai_dataset_summary_prompt"
>;

const CONFIG_KEY_PROPS: {
  [key in AISettingsKey]: {
    title: string;
    subheading: string;
    configKey: AISettingsKey;
  };
} = {
  ai_common_knowledge: {
    title: "Common Knowledge",
    subheading:
      "Context provided to all AI-powered features across your organization.",
    configKey: "ai_common_knowledge",
  },
  ai_dataset_summary_prompt: {
    title: "Dataset Summary Preferences",
    subheading:
      "Instructions considered when generating summaries of datasets.",
    configKey: "ai_dataset_summary_prompt",
  },
} as const;

export function AISettings() {
  const theme = useTheme();
  const { currentOrganization } = useAuth();
  const updateOrgConfig = useUpdateOrgConfig();

  // State for form values
  const [formValues, setFormValues] = React.useState<Record<string, string>>(
    {},
  );
  const [hasChanges, setHasChanges] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  // Fetch current config values
  const datasetPromptQuery = useOrgConfig<string>(
    currentOrganization?.org_id,
    CONFIG_KEY_PROPS.ai_dataset_summary_prompt.configKey,
  );
  const commonKnowledgeQuery = useOrgConfig<string>(
    currentOrganization?.org_id,
    CONFIG_KEY_PROPS.ai_common_knowledge.configKey,
  );

  // Initialize form values when queries complete
  React.useEffect(() => {
    if (!datasetPromptQuery.isLoading && !commonKnowledgeQuery.isLoading) {
      const initialValues = {
        [CONFIG_KEY_PROPS.ai_dataset_summary_prompt.configKey]:
          datasetPromptQuery.data || "",
        [CONFIG_KEY_PROPS.ai_common_knowledge.configKey]:
          commonKnowledgeQuery.data || "",
      };
      setFormValues(initialValues);
    }
  }, [
    datasetPromptQuery.data,
    datasetPromptQuery.isLoading,
    commonKnowledgeQuery.data,
    commonKnowledgeQuery.isLoading,
  ]);

  const handleInputChange = (key: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    if (!currentOrganization?.org_id) {
      return;
    }

    setErrorMessage(null);
    try {
      // Save all config values
      await Promise.all(
        Object.entries(formValues).map(([key, value]) =>
          updateOrgConfig.mutateAsync({
            orgId: currentOrganization.org_id,
            key,
            value,
          }),
        ),
      );

      setHasChanges(false);
      setSuccessMessage("AI settings saved successfully!");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      // Display the error message
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to save AI settings",
      );
    }
  };

  const handleReset = () => {
    const initialValues = {
      [CONFIG_KEY_PROPS.ai_dataset_summary_prompt.configKey]:
        datasetPromptQuery.data || "",
      [CONFIG_KEY_PROPS.ai_common_knowledge.configKey]:
        commonKnowledgeQuery.data || "",
    };
    setFormValues(initialValues);
    setHasChanges(false);
    setSuccessMessage(null);
  };

  const isLoading =
    datasetPromptQuery.isLoading || commonKnowledgeQuery.isLoading;

  const hasError = datasetPromptQuery.error || commonKnowledgeQuery.error;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: theme.spacing(4),
        }}
      >
        <CircularProgress size="1.5rem" />
      </Box>
    );
  }

  if (hasError) {
    return (
      <SettingsPageLayout>
        <Alert severity="error" sx={{ marginBottom: theme.spacing(2) }}>
          Failed to load AI settings. Please try again.
        </Alert>
      </SettingsPageLayout>
    );
  }

  return (
    <SettingsPageLayout>
      {successMessage && (
        <Alert severity="success" sx={{ marginBottom: theme.spacing(2) }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ marginBottom: theme.spacing(2) }}>
          {errorMessage}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
        }}
      >
        {/* Which chat models the org offers comes first: it decides what
            every other AI feature on this page runs on. */}
        <CurrentOrgAiModels />

        <Accordion defaultExpanded disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Organization Knowledge</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(3),
              }}
            >
              {Object.values(CONFIG_KEY_PROPS).map((keyProps) => (
                <Box key={keyProps.configKey}>
                  <Typography
                    variant="subtitle1"
                    sx={{ marginBottom: theme.spacing(1) }}
                  >
                    {keyProps.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ marginBottom: theme.spacing(1) }}
                  >
                    {keyProps.subheading}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={formValues[keyProps.configKey] || ""}
                    onChange={(e) =>
                      handleInputChange(keyProps.configKey, e.target.value)
                    }
                    placeholder="Enter human readable instructions as text or markdown"
                    variant="outlined"
                  />
                </Box>
              ))}

              <Box
                sx={{
                  display: "flex",
                  gap: theme.spacing(2),
                }}
              >
                <RobotoLoadingButton
                  variant="contained"
                  onClick={() => {
                    void handleSave();
                  }}
                  disabled={!hasChanges || updateOrgConfig.isPending}
                  loading={updateOrgConfig.isPending}
                  eventName={"AIOrgConfigSaveClicked"}
                >
                  Save Changes
                </RobotoLoadingButton>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={!hasChanges || updateOrgConfig.isPending}
                  sx={{ textTransform: "none" }}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Personal MCP Connections</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <McpConnections />
          </AccordionDetails>
        </Accordion>
      </Box>
    </SettingsPageLayout>
  );
}
